import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import { toast } from "react-toastify";
import { Modal, Alert, Button } from "components/common";

import { INPUT_CLS as dateInputClass } from 'helpers/formStyles';
/**
 * Shared Tailwind confirmation modal for the three iHRM employee sync flows
 * (new / title-changed / terminal). Each flow only differs by title, warning
 * message and the redux thunk to dispatch — everything else (date range form,
 * validation, submit handling) is identical.
 */
const SyncEmployeeConfirm = ({ showModal, setshowModal, title, message, action }) => {
  const dispatch = useDispatch();
  const { control, handleSubmit, formState: { errors, isSubmitting }, setValue } = useForm();

  useEffect(() => {
    const today = new Date();
    setValue("from_date", today);
    setValue("to_date", today);
  }, [setValue]);

  const closeModal = () => setshowModal(false);

  const onSubmit = async (data) => {
    try {
      await dispatch(action(data)).unwrap();
      closeModal();
    } catch (error) {
      toast.error(error?.message || "Sync failed. Please try again.");
    }
  };

  const footer = (
    <>
      <Button variant="ghost" size="sm" onClick={closeModal}>Cancel</Button>
      <Button variant="primary" size="sm" loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
        Yes, sync now
      </Button>
    </>
  );

  return (
    <Modal show={showModal} onHide={closeModal} title={title} size="md" backdropClick={false} footer={footer}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Alert type="warning" className="tw-mb-4">{message}</Alert>

        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1">Start date</label>
            <Controller
              control={control}
              name="from_date"
              rules={{ required: "Start date is required." }}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={d => field.onChange(adjustToNoon(d))}
                  className={dateInputClass}
                  wrapperClassName="tw-w-full"
                  placeholderText="DD-MM-YYYY"
                  dateFormat="dd-MM-yyyy"
                  fixedHeight
                />
              )}
            />
            {errors.from_date && <p className="tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0">{errors.from_date.message}</p>}
          </div>

          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1">End date</label>
            <Controller
              control={control}
              name="to_date"
              rules={{ required: "End date is required." }}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={d => field.onChange(adjustToNoon(d))}
                  className={dateInputClass}
                  wrapperClassName="tw-w-full"
                  placeholderText="DD-MM-YYYY"
                  dateFormat="dd-MM-yyyy"
                  fixedHeight
                />
              )}
            />
            {errors.to_date && <p className="tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0">{errors.to_date.message}</p>}
          </div>
        </div>
      </form>
    </Modal>
  );
};

SyncEmployeeConfirm.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  action: PropTypes.func.isRequired,
};

export default SyncEmployeeConfirm;
